import { AlertTriangle, Loader2, Settings } from "lucide-react"
import { Trans, useTranslation } from "react-i18next"

import { Button } from "@/shared/ui/button"

export type CloneLaunchVariant = "redirecting" | "fallback" | "error"

interface CloneLaunchScreenProps {
  variant: CloneLaunchVariant
  botHandle: string
  onAction: () => void
}

// The leading "@" would take the paragraph direction in RTL locales and flip the handle to "AI_Celestia_bot@".
const BotHandle: React.FC<{ handle: string }> = ({ handle }) => (
  <bdi
    dir="ltr"
    className="font-semibold text-gray-100">
    {handle}
  </bdi>
)

const SCREEN_ICON_CLASS = "size-[1em] text-2xl text-yellow-500"
const ACTION_BUTTON_CLASS =
  "mt-6 h-11 w-full rounded-xl bg-yellow-500 font-medium text-gray-900 hover-device:hover:bg-yellow-400"

export const CloneLaunchScreen: React.FC<CloneLaunchScreenProps> = ({ variant, botHandle, onAction }) => {
  const { t } = useTranslation()

  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-900 px-6 text-gray-100">
      <div className="w-full max-w-sm text-center">
        {variant === "redirecting" && (
          <>
            <Loader2
              aria-hidden="true"
              focusable="false"
              className="mx-auto size-8 animate-spin text-yellow-500 motion-reduce:animate-none"
            />
            <h1 className="mt-4 text-lg font-semibold text-gray-100">
              {t("clone-launch-redirecting-title", "Opening Celestia settings…")}
            </h1>
          </>
        )}
        {variant === "fallback" && (
          <>
            <div className="mx-auto flex size-12 items-center justify-center rounded-lg bg-gray-800">
              <Settings
                aria-hidden="true"
                focusable="false"
                className={SCREEN_ICON_CLASS}
              />
            </div>
            <h1 className="mt-4 text-lg font-semibold text-gray-100">
              {t("clone-launch-fallback-title", "Settings open in the main Celestia app")}
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              <Trans
                i18nKey="clone-launch-fallback-description"
                defaults="This bot runs on Celestia. Manage your groups in <bot></bot>."
                components={{ bot: <BotHandle handle={botHandle} /> }}
              />
            </p>
            <Button
              className={ACTION_BUTTON_CLASS}
              onClick={onAction}>
              {t("clone-launch-open-settings", "Open settings")}
            </Button>
          </>
        )}
        {variant === "error" && (
          <>
            <div className="mx-auto flex size-12 items-center justify-center rounded-lg bg-gray-800">
              <AlertTriangle
                aria-hidden="true"
                focusable="false"
                className={SCREEN_ICON_CLASS}
              />
            </div>
            <h1
              className="mt-4 text-lg font-semibold text-gray-100"
              role="alert">
              {t("clone-launch-error-title", "Couldn't sign you in")}
            </h1>
            <p className="mt-2 text-sm text-gray-400">
              <Trans
                i18nKey="clone-launch-error-description"
                defaults="Telegram didn't confirm this session. Close the app and open <bot></bot> again."
                components={{ bot: <BotHandle handle={botHandle} /> }}
              />
            </p>
            <Button
              className={ACTION_BUTTON_CLASS}
              onClick={onAction}>
              {t("common:close", "Close")}
            </Button>
          </>
        )}
      </div>
    </main>
  )
}
